import { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { Meeting } from '@/types/Meeting';
import { getPreviousActions } from '@/api/meetings';
import { updateActionStatus } from '@/api/actions';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';

// Helper function to render hashtags in text
const renderHashtagText = (text: string, hashtags: string[]) => {
  const parts = text.split(/(#\w+)/g);
  return (
    <span>
      {parts.map((part, index) => {
        const tag = part.startsWith('#') ? part.slice(1) : null;
        if (tag && hashtags.includes(tag)) {
          return (
            <span key={index} className="inline-block bg-primary/10 text-primary px-2 py-0.5 rounded-full text-sm">
              {part}
            </span>
          );
        }
        return part;
      })}
    </span>
  );
};

interface PreviousActionListProps {
  currentMeetingId: string;
  showCompleted: boolean;
  onShowCompletedChange: (value: boolean) => void;
  showTitle?: boolean;
  onHasActionsChange?: (hasActions: boolean) => void;
}

export function PreviousActionList({ currentMeetingId, showCompleted, onShowCompletedChange, showTitle = false, onHasActionsChange }: PreviousActionListProps) {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [hasActions, setHasActions] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPreviousActions = async () => {
      if (!currentMeetingId) return;
      
      setIsLoading(true);
      try {
        const response = await getPreviousActions(currentMeetingId, showCompleted);
        
        const fetchedMeetings = response.meetings || [];
        setMeetings(fetchedMeetings);
        const hasData = fetchedMeetings.length > 0;
        setHasActions(hasData);
        
        // Report to parent component
        if (onHasActionsChange) {
          onHasActionsChange(hasData);
        }
      } catch (error) {
        console.error('Error fetching previous actions:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load previous actions"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreviousActions();
  }, [currentMeetingId, showCompleted, toast, onHasActionsChange]);

  const handleActionToggle = async (meetingId: string, actionId: string, completed: boolean) => {
    // Validate inputs
    if (!meetingId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Cannot update action - missing meeting ID"
      });
      return;
    }

    try {
      await updateActionStatus(meetingId, actionId, completed);
      
      // Update local state to mark the action as completed without removing it
      setMeetings(currentMeetings => 
        currentMeetings.map(meeting => {
          if (meeting._id === meetingId) {
            return {
              ...meeting,
              actions: meeting.actions.map(action =>
                action._id === actionId 
                  ? { ...action, completed } 
                  : action
              )
            };
          }
          return meeting;
        })
      );
    } catch (error) {
      console.error('Error updating action:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update action status"
      });
    }
  };

  // If there are no previous actions, don't render the component
  if (!hasActions && !isLoading) {
    return null;
  }

  const content = (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="previous-show-completed"
            checked={showCompleted}
            onCheckedChange={onShowCompletedChange}
          />
          <Label htmlFor="previous-show-completed">Show completed</Label>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        expanded && (
          <div className="space-y-8">
            {meetings.map((meeting) => (
              <div key={meeting._id} className="space-y-4">
                <h3
                  className="text-md font-medium cursor-pointer hover:text-primary break-words"
                  onClick={() => navigate(`/app/meetings/${meeting._id}`)}
                >
                  {`${meeting.title} - ${format(new Date(meeting.date), 'dd/MM/yyyy')}`}
                </h3>
                <div className="space-y-3">
                  {meeting.actions && meeting.actions.map((action) => (
                    <div
                      key={action._id}
                      className="flex items-start gap-4 p-4 rounded-lg bg-card hover:bg-secondary/50 transition-colors"
                    >
                      <Checkbox
                        checked={action.completed}
                        onCheckedChange={(checked) => 
                          handleActionToggle(
                            meeting._id,
                            action._id,
                            checked as boolean
                          )
                        }
                        className="mt-1 rounded border-primary"
                      />
                      <div className={`flex-1 break-words ${action.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {renderHashtagText(action.text, action.hashtags || [])}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {meetings.length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                No previous actions found.
              </p>
            )}
            {/* Force clear any potential owner display */}
            <div style={{ display: 'none' }}>
              {/* This should hide any potential Owner display */}
              <span className="sr-only">Hidden content</span>
            </div>
          </div>
        )
      )}
    </div>
  );

  // If showTitle is true, wrap content with title
  if (showTitle) {
    return (
      <div className="space-y-2">
        <h2 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Previous Actions</h2>
        {content}
      </div>
    );
  }

  return content;
}
